import {
  Button,
  ButtonGroup,
  closeDialog,
  Form,
  FormField,
  openDialog,
  Separator,
  Tooltip,
} from 'platform/components';
import {Box, Grid, GridItem, HStack, Icon, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useState} from 'react';

import {head, mergeAll} from 'ramda';

import {usePostStorageLocationAddLocationV2Mutation} from '@dms/api/core';
import {useGetSpacesQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {DmsDataGrid, handleApiError, refreshDmsDatagrid, WAREHOUSE_GRID_CODES} from '@dms/shared';

import type {QueryFilterObject, RowData} from 'features/eag-grid';

const GRID_CODE = WAREHOUSE_GRID_CODES.warehouseArticleStorageLocation;

const STORAGE_LOCATION_CONTEXT_TARGET = 'WAREHOUSE-SPACE';
const CREATE_LOCATION_DIALOG_ID = 'create-location-from-article-dialog';

function extractLocationName(value: unknown, fallback: string): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'object' && value !== null && 'value' in value) {
    const inner = value.value;
    if (typeof inner === 'string') {
      return inner;
    }
    if (typeof inner === 'object' && inner !== null && 'label' in inner) {
      if (typeof inner.label === 'string') {
        return inner.label;
      }
    }
  }
  return fallback;
}

interface AssignLocationDialogProps {
  warehouseId: string;
  onAssign: (storageLocationId: string, locationName: string) => void;
  dialogId: string;
}

export function AssignLocationDialog(props: AssignLocationDialogProps) {
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [selectedLocationName, setSelectedLocationName] = useState<string | null>(null);

  const {data: spaces, isLoading: isSpacesLoading} = useGetSpacesQuery({
    warehouseId: props.warehouseId,
  });
  const firstSpace = spaces?.[0];
  const firstSpaceId = firstSpace?.spaceId;

  // The grid lists free locations across every warehouse by default. Locations belong to spaces,
  // and spaces belong to warehouses, so scope the grid to the warehouse the article is created in
  // by always injecting a mandatory warehouseIds filter.
  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {warehouseIds: [props.warehouseId]}]);

  const handleRowSelectionChange = (rows: RowData[]) => {
    const row = head(rows) ?? null;
    const id = row?.id ?? null;
    setSelectedLocationId(id);
    if (id) {
      setSelectedLocationName(String(extractLocationName(row?.location, id)));
    } else {
      setSelectedLocationName(null);
    }
  };

  const assignAndClose = (storageLocationId: string, locationName: string) => {
    props.onAssign(storageLocationId, locationName);
    closeDialog(props.dialogId);
  };

  const handleAssign = () => {
    if (selectedLocationId && selectedLocationName) {
      assignAndClose(selectedLocationId, selectedLocationName);
    }
  };

  const handleDiscard = () => {
    closeDialog(props.dialogId);
  };

  const handleLocationCreated = () => {
    setSelectedLocationId(null);
    setSelectedLocationName(null);
    refreshDmsDatagrid(GRID_CODE);
  };

  const handleCreateSingleLocation = () => {
    openDialog(
      <CreateSingleLocationDialog
        spaceId={firstSpaceId}
        warehouseId={props.warehouseId}
        spaceName={firstSpace?.name}
        onCreate={handleLocationCreated}
        onCreateAndAssign={assignAndClose}
      />,
      {
        title: i18n.t('entity.tireWarehouses.labels.createSingleLocation'),
        id: CREATE_LOCATION_DIALOG_ID,
      }
    );
  };

  return (
    <VStack spacing={4} height="100%">
      <HStack justify="flex-end">
        <Button
          title={i18n.t('entity.tireWarehouses.labels.createSingleLocation')}
          variant="secondary"
          leftIcon="content/add_circle"
          isLoading={isSpacesLoading}
          onClick={handleCreateSingleLocation}
        />
      </HStack>
      <Box height="55vh">
        <Show when={props.warehouseId !== ''}>
          <DmsDataGrid
            gridCode={GRID_CODE}
            queryModifier={queryModifier}
            onRowSelectionChange={handleRowSelectionChange}
            actionCallback={(params) => {
              // Only handle row click/detail actions
              if (params.actionKey !== 'redirectDetail' && params.actionKey !== 'edit') {
                return;
              }

              const id = Array.isArray(params.rowId) ? head(params.rowId) : params.rowId;
              if (id) {
                setSelectedLocationId(id);
                const locationData = params.getColumnData('location' as never);
                setSelectedLocationName(extractLocationName(locationData?.value, id));
              }
            }}
          />
        </Show>
      </Box>
      <ButtonGroup align="right">
        <Button
          title={i18n.t('general.actions.discard')}
          variant="secondary"
          onClick={handleDiscard}
        />
        <Button
          title={i18n.t('general.actions.assign')}
          isDisabled={!selectedLocationId || !selectedLocationName}
          onClick={handleAssign}
        />
      </ButtonGroup>
    </VStack>
  );
}

// ─── Create single location dialog ────────────────────────────────────────

const createLocationFormSchema = z.object({
  type: z.string(),
  space: z.string().min(1),
  location: z.string().min(1),
  note: z.string(),
  allowMultipleUsage: z.boolean(),
});
type CreateLocationForm = z.infer<typeof createLocationFormSchema>;

type CreateLocationSubmitButton = 'CREATE' | 'CREATE_AND_ASSIGN';

interface CreateSingleLocationDialogProps {
  spaceId?: string;
  warehouseId: string;
  spaceName?: string;
  onCreate: () => void;
  onCreateAndAssign: (storageLocationId: string, locationName: string) => void;
}

function CreateSingleLocationDialog(props: CreateSingleLocationDialogProps) {
  const typeOptions = [
    {label: i18n.t('entity.warehouse.labels.simpleLocationType'), value: 'simple'},
  ];

  const [submittedButton, setSubmittedButton] = useState<CreateLocationSubmitButton | null>(null);

  const [createLocation, createStatus] = usePostStorageLocationAddLocationV2Mutation();
  const {data: spaces} = useGetSpacesQuery({warehouseId: props.warehouseId});

  const spaceOptions = (spaces ?? []).map((space) => ({
    label: space.name,
    value: space.spaceId,
  }));

  const handleClose = () => {
    closeDialog(CREATE_LOCATION_DIALOG_ID);
  };

  const handleCreated = (
    storageLocationId: string,
    locationName: string,
    button: CreateLocationSubmitButton
  ) => {
    handleClose();
    match(button)
      .with('CREATE_AND_ASSIGN', () => props.onCreateAndAssign(storageLocationId, locationName))
      .with('CREATE', () => props.onCreate())
      .exhaustive();
  };

  const submitLocation = async (
    formData: CreateLocationForm,
    button: CreateLocationSubmitButton
  ) => {
    if (createStatus.isLoading) {
      return;
    }
    setSubmittedButton(button);
    await createLocation({
      contextTarget: STORAGE_LOCATION_CONTEXT_TARGET,
      contextId: formData.space,
      body: {
        location: formData.location,
        allowMultipleUsage: formData.allowMultipleUsage,
        note: formData.note || undefined,
      },
    })
      .unwrap()
      .then((response) => {
        if (!response) {
          return;
        }
        handleCreated(response.storageLocationId, formData.location, button);
      })
      .catch(handleApiError);
  };

  const handleSubmit = async (formData: CreateLocationForm) => {
    await submitLocation(formData, 'CREATE_AND_ASSIGN');
  };

  return (
    <Form
      defaultValues={{
        type: 'simple',
        space: props.spaceId ?? '',
        location: '',
        note: '',
        allowMultipleUsage: true,
      }}
      experimentalZodSchema={createLocationFormSchema}
      onSubmit={handleSubmit}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <Grid columns={5}>
            <GridItem>
              <FormField
                type="radio"
                control={control}
                name="type"
                label={i18n.t('general.labels.type')}
                options={typeOptions}
              />
            </GridItem>
            <GridItem>
              <FormField
                type="choice"
                control={control}
                name="space"
                isNotClearable
                label={i18n.t('entity.warehouse.labels.space')}
                options={spaceOptions}
              />
            </GridItem>
          </Grid>
          <Separator />
          <FormField
            type="text"
            control={control}
            name="location"
            label={i18n.t('general.labels.name')}
            isRequired
          />
          <FormField
            type="textarea"
            control={control}
            name="note"
            label={i18n.t('general.labels.note')}
            isResizable
          />
          <HStack spacing={1} align="center">
            <FormField
              type="checkbox"
              control={control}
              name="allowMultipleUsage"
              label={i18n.t('entity.tireWarehouses.labels.unlimitedUsage')}
            />
            <Tooltip description={i18n.t('entity.tireWarehouses.labels.unlimitedUsage.tooltip')}>
              <Icon value="action/help" color="text.secondary" size={4} tabIndex={0} />
            </Tooltip>
          </HStack>
          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.discard')}
              variant="secondary"
              onClick={handleClose}
            />
            <Button
              title={i18n.t('general.actions.create')}
              variant="secondary"
              isLoading={createStatus.isLoading && submittedButton === 'CREATE'}
              isDisabled={createStatus.isLoading && submittedButton !== 'CREATE'}
              onClick={formApi.handleSubmit((formData) => submitLocation(formData, 'CREATE'))}
            />
            <Button
              title={i18n.t('entity.warehouse.actions.createAndAssign')}
              type="submit"
              isLoading={createStatus.isLoading && submittedButton === 'CREATE_AND_ASSIGN'}
              isDisabled={createStatus.isLoading && submittedButton !== 'CREATE_AND_ASSIGN'}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
